import { Chip, LinearProgress, Tooltip, Typography } from '@mui/material'
import _ from 'lodash'
import { useCallback } from 'react'
import API_URLS from '../../backend/ApiUrls'
import { IContract } from '../../definitions/Contract'
import ContractsManager, { CSimilarity } from '../../managers/ContractsManager'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import { Cell, Column, Row, SmartTable } from '../controls/table'

type ContractRow = IContract & { csi: CSimilarity, counterpartySignatory: any }

interface P {
    setFilteredContracts: (rows: ContractRow[]) => void
}

export default function Contracts({ setFilteredContracts }: P) {
    const dispatch = useAppDispatch()

    const getStatusChip = (status: string) => {
        switch (status) {
            case 'Live':
                return <Chip label='Live' color='success' size='small' />
            case 'Draft':
                return <Chip label='Draft' color='default' size='small' />
            case 'Archived':
                return <Chip label='Archived' color='info' size='small' />
        }
    }

    const handleVisibleRowsChange = useCallback((rows: ContractRow[]) => {
        setFilteredContracts(rows)
    }, [])

    const columns: Column<ContractRow>[] = [
        { id: 'id', label: 'Id', isPK: true, hidden: true },
        { id: 'name', label: 'Name' },
        { id: 'startDate', label: 'Start date', type: 'date' },
        { id: 'endDate', label: 'End Date', type: 'date' },
        {
            id: 'status.name',
            label: 'Contract Status',
            customRenderer: x => {
                const status = x.value as string
                return getStatusChip(status)
            }
        },
        {
            id: 'productType.name',
            label: 'Product Type',
            customRenderer: x => {
                if (!x.value) return null
                return <Chip color='secondary' label={x.value} size='small' />
            }
        },
        { id: 'externalId', label: 'Business Key' },
        {
            id: 'csi',
            label: 'C.S.I.',
            type: 'number',
            customRenderer: cell => {
                const cs = cell.value as any as CSimilarity
                if (!cs) return null
                return (
                    <Tooltip
                        title={(
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {cs.tos?.map(x => (
                                    <div
                                        key={x.uuid}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 'var(--unit)'
                                        }}>
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <Typography
                                                fontSize={14}
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                {x.name}
                                            </Typography>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <LinearProgress
                                                variant='determinate'
                                                value={x.score * 100}
                                                style={{ width: '100%', height: 6, borderRadius: 4 }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}>
                        <Typography>{_.round(cs?.avg, 3)}</Typography>
                    </Tooltip>
                )
            }
        },
        { id: 'reporting.reports.name', label: 'Reports', hidden: true },
        { id: 'reporting.dataSharing.name', label: 'Data Sharing', hidden: true }
    ]

    const handleRowClick = (indexRow: number, cells: Cell[], row: Row, item: ContractRow) => {
        if (item) {
            dispatch(
                changeRoute({
                    route: 'contract-details',
                    params: { contractUuid: item.uuid, contractId: item.id },
                    customRouteName: item.name
                })
            )
        } else {
            console.error('Items not found in the rows.')
        }
    }
    const searchAll = ['name', 'externalId', 'status.name', 'productType.name']

    return (
        <SmartCard table={true}>
            <SmartTable<ContractRow>
                key='contracts_table'
                columns={columns}
                noCollapse
                onVisibleRowsChange={handleVisibleRowsChange}
                onClickRow={handleRowClick}
                datasource={{
                    remote: {
                        url: API_URLS.os.getContracts,
                        onSuccess: response => {
                            let newContractRows: ContractRow[] = []
                            if (response && Array.isArray(response.items) && response.items.length > 0) {
                                const cs = ContractsManager.crossSimilarities(response.items as unknown as ContractRow[])
                                newContractRows = (response.items as ContractRow[]).map((x, i) => ({ ...x, csi: cs[i] }))
                            }
                            setFilteredContracts(newContractRows)
                            return { ...response, items: newContractRows } as any
                        }
                    }
                }}
                toolbar={{
                    columns: true,
                    search: searchAll,
                    filters: true,
                    export: {
                        fileName: 'Contracts_table'
                    }
                }}
            />
        </SmartCard>
    )
}
